import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import "./QRScanner.css";
import { toast } from "react-toastify";
import { Button, Modal, Tooltip } from "antd";
import { Scrollbar } from "react-scrollbars-custom";
import {
  scanPetrifilm,
  addUserMicroImage,
  migrateResults,
} from "../../../../actions/micro";
import { checkReadyReports } from "../../../../actions/sendReports";
import ScannerTable from "./ScannerTable";
import ScanArea from "./ScanArea";
import PetrifilmImageSection from "./PetrifilmImageSection";
import {
  buildMicroscannerS3ObjectKey,
  getFirstPetrifilmImagePathFromSamples,
  resolvePetrifilmImagePreviewUrl,
} from "./Helpers";
import { getUser, uploadFileToS3 } from "../../../../utils/helpers";
import { s3AutomationPrivate } from "../../../../utils/aws";
import LoadingGif from "../../../../assets/images/loading.gif";

/** QR payload is wrapped: starts with backtick, ends with `?` (see scan effect). */
const SCANNER_PREFIX_CHAR = "`";
const SCANNER_SUFFIX_CHAR = "?";
/**
 * Single object for petrifilm image UX + scanner context (keeps related updates together).
 *
 */
const emptyPetrifilmScannerState = () => ({
  // After a mapped row saves, `true` until PetrifilmImageSection consumes and sets false.
  uploadActivityRequested: false,
  // WO / genista / petrifilm fields from the current scan — required for S3 key + addUserMicroImage.
  microScannerQrContext: null,
  // API `user_image_path` (S3 object key) from micro_scanner or after register.
  microScannerUserImagePath: "",
  // Resolved preview URL for microScannerUserImagePath (presign or pass-through); revoked on change/unmount.
  userPetrifilmImagePreviewUrl: null,
  // True while resolving presigned / S3 blob preview for microScannerUserImagePath.
  petrifilmPreviewLoading: false,
  // Computed S3 object key for a newly selected local image before upload/register.
  pendingLocalPetrifilmObjectKey: null,
});

/** Rows still in the scanner workflow, presets must not overwrite e.g. Ready + saved counts. */
const eligibleForScannerCountPreset = (sample) => sample.status === "Pending"
  || sample.status === "Retest"
  || sample.status === "TNTC"
  || sample.mapping === false;

/** True when API row already represents a TNTC raw count (do not apply Clean -> 0 on top of it). */
const sampleHasTntcCountFromServer = (sample) => {
  const token = String(sample.counts ?? "").toLowerCase();
  return sample.status === "TNTC" || token === "tntc";
};

/** Apply Clean/TNTC presets to table rows when opening the modal (initial scan only). */
const mapScanResultToSamples = (scanResult, scanMode = {}) => {
  const { applyCleanPresetCounts, applyTntcPreset } = scanMode;
  return scanResult.map((sample, index) => {
    const base = {
      ...sample,
      key: index,
      recheck: false,
      edit: false,
      isTntcChecked: sampleHasTntcCountFromServer(sample),
    };
    if (applyTntcPreset) {
      if (eligibleForScannerCountPreset(sample)) {
        return {
          ...base,
          isTntcChecked: true,
          counts: "tntc",
        };
      }
      return base;
    }
    if (applyCleanPresetCounts) {
      if (eligibleForScannerCountPreset(sample) && !sampleHasTntcCountFromServer(sample)) {
        return { ...base, counts: "0", isTntcChecked: false };
      }
    }
    return base;
  });
};

/** Merge addUserMicroImage response rows into the table by test_in_sample_id; preserves row key. */
const mergeSamplesWithAddUserMicroImageResult = (previousSamples, apiRows) => {
  if (!Array.isArray(apiRows) || !apiRows.length) {
    return previousSamples;
  }
  return previousSamples.map((row) => {
    const match = apiRows.find(
      (apiRow) => apiRow.test_in_sample_id != null
        && row.test_in_sample_id != null
        && apiRow.test_in_sample_id === row.test_in_sample_id,
    );
    if (!match) {
      return row;
    }
    const merged = {
      ...row,
      ...match,
      key: row.key,
      edit: false,
    };
    const countToken = String(merged.counts ?? "").toLowerCase();
    merged.isTntcChecked = merged.status === "TNTC" || countToken === "tntc";
    return merged;
  });
};

const QRScanner = () => {
  // Modal & scan input
  const [showCountsModal, setShowCountsModal] = useState(false);
  const [qrData, setQrData] = useState("");
  const [samples, setSamples] = useState([]);
  const [cleanChecked, setCleanChecked] = useState(false);
  const [tntcChecked, setTntcChecked] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [scanningLoading, setScanningLoading] = useState(false);
  const [fetchHierarchyLoading, setFetchHierarchyLoading] = useState(false);
  const [migrateLoading, setMigrateLoading] = useState(false);

  // Hierarchy-resolve flow (modal title Submit; petrifilm hidden while true). Setter exists for table; not toggled on from here today. ---
  const [isResolvingHierarchy, setIsResolvingHierarchy] = useState(false);
  // When true, all hierarchy-failed rows have been saved once — enables modal Submit.
  const [allSamplesRechecked, setAllSamplesRechecked] = useState(false);
  // True while upload_single_counts runs during hierarchy resolve (disables Submit briefly).
  const [resolveHierarchyRecheckLoading, setResolveHierarchyRecheckLoading] = useState(false);

  // TNTC: isTntcTable = multi-row + TNTC toggle; isTntcScannerMode = any TNTC scan with ≥1 row (includes single row). ScannerTable needs OR for column + submit behavior.
  const [isTntcTable, setIsTntcTable] = useState(false);
  const [isTntcScannerMode, setIsTntcScannerMode] = useState(false);

  const [petrifilmState, setPetrifilmState] = useState(emptyPetrifilmScannerState);
  const [lastSubmittedMappedRowData, setLastSubmittedMappedRowData] = useState(null);

  const qrInputRef = useRef();
  // Pending File from PetrifilmImageSection before S3 upload + register.
  const petrifilmImageFileRef = useRef(null);
  // Cleanup for presigned preview URL from resolvePetrifilmImagePreviewUrl.
  const userPetrifilmPreviewRevokeRef = useRef(() => {});
  const user = getUser();

  const canUploadPetrifilmImage = useMemo(
    () => samples.some((s) => s.mapping),
    [samples],
  );

  /** Load or clear presigned preview when microScannerUserImagePath changes. */
  useEffect(() => {
    let cancelled = false;
    userPetrifilmPreviewRevokeRef.current();
    userPetrifilmPreviewRevokeRef.current = () => {};
    setPetrifilmState((previous) => ({
      ...previous,
      userPetrifilmImagePreviewUrl: null,
    }));
    const rawPath = petrifilmState.microScannerUserImagePath;
    const path = typeof rawPath === "string" ? rawPath.trim() : "";

    if (!path) {
      setPetrifilmState((previous) => ({ ...previous, petrifilmPreviewLoading: false }));
      return () => {
        cancelled = true;
      };
    }

    setPetrifilmState((previous) => ({ ...previous, petrifilmPreviewLoading: true }));

    (async () => {
      try {
        const { previewUrl, revoke } = await resolvePetrifilmImagePreviewUrl(path);
        if (cancelled) {
          if (typeof revoke === "function") {
            revoke();
          }
          return;
        }
        userPetrifilmPreviewRevokeRef.current = typeof revoke === "function" ? revoke : () => {};
        setPetrifilmState((previous) => ({
          ...previous,
          userPetrifilmImagePreviewUrl: previewUrl || null,
          petrifilmPreviewLoading: false,
        }));
      } catch {
        if (!cancelled) {
          setPetrifilmState((previous) => ({ ...previous, petrifilmPreviewLoading: false }));
        }
      }
    })();

    return () => {
      cancelled = true;
      userPetrifilmPreviewRevokeRef.current();
      userPetrifilmPreviewRevokeRef.current = () => {};
      setPetrifilmState((previous) => ({ ...previous, petrifilmPreviewLoading: false }));
    };
  }, [petrifilmState.microScannerUserImagePath]);

  /** Opens counts modal with mapped rows and optional scanner context for petrifilm upload. */
  const openCountsModalWithScanResult = (
    scanResult,
    nextModalTitle,
    scanMode = {},
    scannerQrContextPayload = null,
  ) => {
    setModalTitle(nextModalTitle);
    setSamples(mapScanResultToSamples(scanResult, scanMode));
    setPetrifilmState((previous) => ({ ...previous, microScannerQrContext: scannerQrContextPayload }));
    setShowCountsModal(true);
  };

  /** Reload current modal rows from /micro_scanner/ after mapping changes. */
  const refreshSamplesFromMicroscanner = useCallback(async () => {
    const context = petrifilmState.microScannerQrContext;
    if (!context) {
      return;
    }

    const payload = {
      wo_id: context.wo_id,
      genista_id: context.genista_id,
      petrifilm: context.petrifilmTisPk || context.petrifilm,
      petrifilm_code: context.petrifilm_code,
      df: context.df,
      user_id: getUser()?.id,
      counts: undefined,
    };

    const {
      success,
      result,
      message,
      user_image_path: scannerUserImagePathRaw,
    } = await scanPetrifilm(payload);

    if (!success) {
      toast.error(message || "Failed to refresh scanner table.");
      return;
    }

    const scannerUserImagePathNormalized = typeof scannerUserImagePathRaw === "string" && scannerUserImagePathRaw.trim()
      ? scannerUserImagePathRaw.trim()
      : "";

    setSamples(mapScanResultToSamples(result || []));
    setPetrifilmState((previous) => ({
      ...previous,
      microScannerUserImagePath: scannerUserImagePathNormalized,
    }));
  }, [petrifilmState.microScannerQrContext]);

  /**
   * Remove leading zeros in string. If the string has 1 underscore, leading zeros only removed from the part after the underscore.
   * ex: "00123" --> "123", "00123_00456" --> "00123_456"
   * @param {string} string
   * @returns {string}
   */
  const removeLeadingZeros = (string) => {
    if (!string) {
      return "";
    }
    // Remove leading zeros; if the string is only zeros, keep the last zero.
    const removeLeadingZerosRegex = /^(?:0+(?=[^0])|0+(?=0$))/g;
    const splitByUnderscore = string.split("_");
    if (splitByUnderscore.length === 2) {
      return `${splitByUnderscore[0]}_${splitByUnderscore[1].replace(removeLeadingZerosRegex, "")}`;
    }
    return string.replace(removeLeadingZerosRegex, "");
  };

  /** Reset scanner + modal state and refocus the hardware input. */
  const handleReset = () => {
    setQrData("");
    setSamples([]);
    setModalTitle("");
    setIsResolvingHierarchy(false);
    setAllSamplesRechecked(false);
    petrifilmImageFileRef.current = null;
    setIsTntcScannerMode(false);
    setPetrifilmState(emptyPetrifilmScannerState());
    setLastSubmittedMappedRowData(null);
    setShowCountsModal(false);
    qrInputRef.current.focus();
  };

  const handlePetrifilmImageFileChange = useCallback((fileOrNull) => {
    petrifilmImageFileRef.current = fileOrNull;
    if (!fileOrNull) {
      setPetrifilmState((previous) => ({ ...previous, pendingLocalPetrifilmObjectKey: null }));
      return;
    }
    const context = petrifilmState.microScannerQrContext;
    if (!context) {
      setPetrifilmState((previous) => ({ ...previous, pendingLocalPetrifilmObjectKey: null }));
      return;
    }
    const objectKey = buildMicroscannerS3ObjectKey({
      woId: context.wo_id,
      genistaId: context.genista_id,
      petrifilmCode: context.petrifilm_code,
      df: context.df,
      petrifilm: context.petrifilm,
      originalFileName: fileOrNull.name,
    });
    setPetrifilmState((previous) => ({ ...previous, pendingLocalPetrifilmObjectKey: objectKey }));
  }, [petrifilmState.microScannerQrContext]);

  /** Clears uploadActivityRequested after the petrifilm upload activity modal closes. */
  const clearPetrifilmUploadActivityRequest = useCallback(() => {
    setPetrifilmState((previous) => ({ ...previous, uploadActivityRequested: false }));
  }, []);

  /** On upload activity modal submit: optional S3 upload, addUserMicroImage, merge rows. */
  const handleAfterPetrifilmUploadModalCommit = async () => {
    const mappedRows = samples.filter((row) => row.mapping);
    if (!mappedRows.length) {
      return { success: true };
    }
    const userId = getUser().id;
    let imagePath = getFirstPetrifilmImagePathFromSamples(samples)
      || petrifilmState.microScannerUserImagePath;
    const localFile = petrifilmImageFileRef.current;
    if (localFile) {
      const context = petrifilmState.microScannerQrContext;
      const key = buildMicroscannerS3ObjectKey({
        woId: context.wo_id,
        genistaId: context.genista_id,
        petrifilmCode: context.petrifilm_code,
        df: context.df,
        petrifilm: context.petrifilm,
        originalFileName: localFile.name,
      });
      const uploadResult = await uploadFileToS3(
        {
          file: localFile,
          key,
          type: localFile.type || "image/jpeg",
        },
        s3AutomationPrivate,
      );
      if (!uploadResult.success) {
        toast.error("Could not upload petrifilm image.");
        return { success: false };
      }
      // S3 POST returns object key only (same as presign fields.key), not the bucket URL.
      const uploadedKey = uploadResult.path;
      imagePath = typeof uploadedKey === "string" ? uploadedKey.trim() : "";
    }
    if (!imagePath) {
      toast.error("No image path available to save.");
      return { success: false };
    }
    const normalizedPath = typeof imagePath === "string" ? imagePath.trim() : String(imagePath).trim();

    const submittedMappedRowData = lastSubmittedMappedRowData;
    const context = petrifilmState.microScannerQrContext;
    const registerResult = await addUserMicroImage({
      wo_id: context.wo_id,
      wo_pk: submittedMappedRowData.woPk,
      genista_id: context.genista_id,
      petrifilm: context.petrifilm,
      petrifilm_code: context.petrifilm_code,
      df: context.df,
      user_id: userId,
      img_path: normalizedPath,
    });
    if (!registerResult.success) {
      toast.error(registerResult.message || "Failed to register petrifilm image.");
      return { success: false };
    }
    if (registerResult.message) {
      toast.success(registerResult.message);
    }
    setSamples((previous) => mergeSamplesWithAddUserMicroImageResult(previous, registerResult.result));
    const registeredPath = registerResult.user_image_path;
    const responsePath = (typeof registeredPath === "string" && registeredPath.trim())
      ? registeredPath.trim()
      : normalizedPath;
    petrifilmImageFileRef.current = null;
    setPetrifilmState((previous) => ({
      ...previous,
      microScannerUserImagePath: responsePath,
      pendingLocalPetrifilmObjectKey: null,
    }));
    return { success: true };
  };

  // After a mapped row’s count save: open the petrifilm upload activity modal (close only via Submit inside that modal).
  const handleRowCountSubmitSuccess = (nextSamples, submittedRow) => {
    if (submittedRow.mapping) {
      setLastSubmittedMappedRowData({
        woPk: submittedRow.wo,
        df: submittedRow.df,
      });
    }
    if (!nextSamples.some((row) => row.mapping)) {
      return;
    }
    setPetrifilmState((previous) => ({
      ...previous,
      uploadActivityRequested: true,
    }));
  };

  const handleCloseModal = (event) => {
    // Hierarchy-resolve modal: block closing with Escape until flow is redesigned.
    if (event.key === "Escape" && isResolvingHierarchy) {
      return;
    }
    // Petrifilm upload activity is open (mandatory upload has keyboard=false; Escape would reach here).
    if (event.key === "Escape" && petrifilmState.uploadActivityRequested) {
      return;
    }
    handleReset();
  };

  const checkPetrifilmCodeValue = (values) => {
    const returnValue = {
      petrifilmName: null,
      petrifilmTisPk: null,
    };
    const petrifilmCodeValue = values.slice(4).join(" ");
    const petrifilmTisPk = Number(petrifilmCodeValue);
    if (Number.isNaN(petrifilmTisPk)) { // Old version of the QR code format has only petrifilm name.
      returnValue.petrifilmName = petrifilmCodeValue;
    } else {
      returnValue.petrifilmTisPk = petrifilmTisPk; // New version of the QR code format has petrifilm TIS PK.
    }
    return returnValue;
  };
  /**
   * On `qrData` change: when the string ends with `?`, parse delimited QR and call micro_scanner.
   * Clean/TNTC: omit `counts` on the request so the backend returns data only; 0 / tntc come from mapScanResultToSamples + row submit.
   * No success toast here — counts are not final until each row’s submit.
   * Dependency: `[qrData]` only — clean/tntc toggles are read from the closure when the scan runs (same as legacy behavior).
   */
  useEffect(() => {
    const onScan = async () => {
      setScanningLoading(true);
      if (qrData && qrData[qrData.length - 1] === SCANNER_SUFFIX_CHAR) {
        const prefixIdx = qrData.lastIndexOf(SCANNER_PREFIX_CHAR);
        const suffixIdx = qrData.lastIndexOf(SCANNER_SUFFIX_CHAR);

        if (prefixIdx < 0 || suffixIdx < 0 || prefixIdx >= suffixIdx) {
          toast.error("Unable to parse QR code");
          setQrData("");
          setScanningLoading(false);
          return;
        }
        const qrDataString = qrData.slice(prefixIdx + 1, suffixIdx).trim();
        const values = qrDataString.split(" ");

        if (values.length < 5) {
          toast.error("Unable to parse QR code");
          setQrData("");
          setScanningLoading(false);
          return;
        }

        const wo = removeLeadingZeros(values[0]);
        const genistaId = removeLeadingZeros(values[1]);
        const petrifilmCode = values[2] === "-" ? "" : values[2];
        const df = removeLeadingZeros(values[3]);
        // Petrifilm name may contain spaces — join remaining tokens.
        const { petrifilmName, petrifilmTisPk } = checkPetrifilmCodeValue(values);

        const payload = {
          wo_id: wo,
          genista_id: genistaId,
          petrifilm: petrifilmName || petrifilmTisPk,
          petrifilm_code: petrifilmCode,
          df,
          user_id: getUser()?.id,
        };
        if (!cleanChecked && !tntcChecked) {
          payload.counts = undefined;
        }

        const {
          success, result, message, mapping, user_image_path: scannerUserImagePathRaw,
        } = await scanPetrifilm(payload);

        const scannerUserImagePathNormalized = typeof scannerUserImagePathRaw === "string" && scannerUserImagePathRaw.trim()
          ? scannerUserImagePathRaw.trim()
          : null;

        if (success) {
          // Multi-row TNTC: different modal title + table uses isTntcTable for TNTC column.
          const showTntcPopup = result?.length > 1 && tntcChecked;
          // Single- or multi-row TNTC scan: ScannerTable needs this for TNTC column + submit when row count is 1.
          const tntcScannerMode = tntcChecked && Array.isArray(result) && result.length > 0;
          setIsTntcTable(showTntcPopup);
          setIsTntcScannerMode(tntcScannerMode);
          if (result?.length) {
            // Use the petirfilm value from the scanPetrifilm() response. It's because the new inline label format won't have the petrifilm name.
            const petrifilmNameFromResponse = result.find((row) => row.petrifilm)?.petrifilm;
            if (!petrifilmNameFromResponse) {
              toast.error("No corresponding Petrifilm name found in the response.");
              setQrData("");
              return;
            }
            setPetrifilmState((previous) => ({
              ...previous,
              microScannerUserImagePath: scannerUserImagePathNormalized,
            }));
            setQrData(qrDataString);
            let modalTitleNext = "Submit Raw Counts";
            if (showTntcPopup) {
              modalTitleNext = "Submit Raw Counts or Confirm TNTC";
            } else if (!mapping) {
              modalTitleNext = "Submit Raw Counts and Petrifilm Mapping";
            } else {
              modalTitleNext = "Review counts and petrifilm image";
            }
            openCountsModalWithScanResult(result, modalTitleNext, {
              applyCleanPresetCounts: cleanChecked,
              applyTntcPreset: tntcChecked,
            }, {
              wo_id: wo,
              genista_id: genistaId,
              petrifilm: petrifilmNameFromResponse,
              petrifilmTisPk,
              petrifilm_code: petrifilmCode,
              df,
            });
          } else {
            setPetrifilmState((previous) => ({ ...previous, microScannerUserImagePath: "" }));
            toast.info("No corresponding Micro Tests. No actions required.");
            setQrData("");
          }
        } else if (message !== "Something went wrong.") {
          toast.warning(message);
          setQrData("");
        } else {
          toast.error(message);
          setQrData("");
        }
      }
      setScanningLoading(false);
    };
    onScan();
  }, [qrData]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * After samples update during hierarchy resolve, clear recheck loading so Submit state matches fresh rows.
   * Dependency: [samples] only.
   */
  useEffect(() => {
    if (isResolvingHierarchy && resolveHierarchyRecheckLoading) {
      setResolveHierarchyRecheckLoading(false);
    }
  }, [samples]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Migrate micro results then trigger ready-report batch if applicable. */
  const handleMigrate = async () => {
    await migrateResults();
    checkReadyReports();
  };

  /**
   * Hierarchy modal Submit: migrate + reset (POST resolve hierarchy was previously here).
  * Call migrate api. If successful, check ready reports and call batch reports api if there are any.
  */
  // const handlePostResolveHierarchy = async () => {
  //   const sampleList = samples.map(({
  //     wo, sample_id, analysis_display, df, counts, result, default_res, qc_status,
  //   }) => ({
  //     wo, sample_id, analysis_display, df, counts, result, default_res, qc_status,
  //   }));
  //   const { success, message } = await postResolveHierarchySamples(sampleList);
  //   if (!success) {
  //     toast.error(message);
  //   }
  //   return null;
  // };

  /** Hierarchy modal Submit: migrate + reset (POST resolve hierarchy was previously here). */
  const handleModalSubmit = async () => {
    setMigrateLoading(true);
    // await handlePostResolveHierarchy();
    await handleMigrate();
    setMigrateLoading(false);
    handleReset();
  };

  /** Scan area control: migrate without opening the counts modal. */
  const handleFetchHierarchyFailedSamples = async () => {
    setFetchHierarchyLoading(true);
    await handleMigrate();
    setFetchHierarchyLoading(false);
  };

  return (
    <div className="QRScanner">
      <div className="QRScanner__InnerContainer--small">
        <ScanArea
          qrData={qrData}
          setQrData={setQrData}
          cleanChecked={cleanChecked}
          setCleanChecked={setCleanChecked}
          tntcChecked={tntcChecked}
          setTntcChecked={setTntcChecked}
          showCountsModal={showCountsModal}
          qrInputRef={qrInputRef}
          scanningLoading={scanningLoading}
          handleFetchHierarchyFailedSamples={handleFetchHierarchyFailedSamples}
        />
      </div>
      <Modal
        title={(
          <div className="QRScanner__Modal__Title">
            <span>{modalTitle}</span>
            {isResolvingHierarchy && (
              <Tooltip title={!allSamplesRechecked || resolveHierarchyRecheckLoading ? "All counts must be saved before submitting" : ""}>
                <Button
                  className="QRScanner__Modal__CloseButton"
                  onClick={handleModalSubmit}
                  loading={migrateLoading}
                  disabled={!allSamplesRechecked || resolveHierarchyRecheckLoading}
                >
                  Submit
                </Button>
              </Tooltip>
            )}
          </div>
        )}
        open={showCountsModal}
        maskClosable={false}
        destroyOnClose
        onCancel={(event) => handleCloseModal(event)}
        footer={null}
        centered
        className={`QRScanner__Modal ${isResolvingHierarchy ? "QRScanner__Modal--pending" : ""}`}
        rootClassName="QRScanner__Modal__Root"
        width="80vw"
      >
        <Scrollbar className="QRScanner__Modal__ScrollbarContainer">
          {/* Petrifilm block hidden during hierarchy-resolve mode only. */}
          {!isResolvingHierarchy && (
            <PetrifilmImageSection
              canUploadImage={canUploadPetrifilmImage}
              petrifilmImageMandatory={Boolean(user?.permissionMap.require_petrifilm_image_micro_scanner)}
              existingPetrifilmImageUrlFromServer={petrifilmState.userPetrifilmImagePreviewUrl}
              petrifilmPreviewLoading={petrifilmState.petrifilmPreviewLoading}
              storedPetrifilmObjectKey={petrifilmState.microScannerUserImagePath}
              pendingLocalPetrifilmObjectKey={petrifilmState.pendingLocalPetrifilmObjectKey}
              onPetrifilmImageFileChange={handlePetrifilmImageFileChange}
              petrifilmUploadActivityRequested={petrifilmState.uploadActivityRequested}
              onPetrifilmUploadActivityRequestConsumed={clearPetrifilmUploadActivityRequest}
              onAfterUploadModalCloseCommit={handleAfterPetrifilmUploadModalCommit}
            />
          )}
          <ScannerTable
            samples={samples}
            setSamples={setSamples}
            handleReset={handleReset}
            setAllSamplesRechecked={setAllSamplesRechecked}
            setResolveHierarchyRecheckLoading={setResolveHierarchyRecheckLoading}
            isResolvingHierarchy={isResolvingHierarchy}
            migrateLoading={migrateLoading}
            isTntcTable={isTntcTable}
            isTntcScannerMode={isTntcScannerMode}
            onRowCountSubmitSuccess={handleRowCountSubmitSuccess}
            onMappingRemoveSuccess={refreshSamplesFromMicroscanner}
          />
        </Scrollbar>
      </Modal>
      {fetchHierarchyLoading && (
        <div className="loadingIndicator">
          <img src={LoadingGif} alt="Loading gif" />
        </div>
      )}
    </div>
  );
};

export default QRScanner;
